import React from "react";
import { styled } from "@linaria/react";
import Menu from "@ui/Menu";
import { color, fonts } from "@ui";
import Option, { OptionGroup } from "@ui/Option";
import type { Project } from "../../../../api/useProjects";
import { useModals } from "@ui/lib/hooks/useModal";
import Modal from "@ui/Modal";
import t from "../../../../i18n";
import CreateUpdateModalForm from "../Setup/CreateUpdateModalForm";
import { HIDE_ON_EXPORT } from "@ui/lib/downloadPage";
import DeleteModalForm from "../Setup/DeleteModalForm";
import download from "downloadjs";
import type { Setup } from "../../../../api/useSetups";
import { useGet } from "../../../../api/useCall";
import { useCanUserDoOnProject } from "../../../canUserDo";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { DeleteBinLine, DownloadLine, FileCopyLine, MoreFill, PencilLine } from "@ui/IconV2/react";
import DuplicateModalForm from "./DuplicateModalForm";
import { generateExportFileName } from "diagnostics-lib/string";
const StyledMenu = styled(Menu)`
  font: ${fonts.body2};
`;

interface P {
  project: Project;
  setup: Setup;
  setups: Setup[];
}

function SecondaryDropdownMenu({ project, setup, setups }: P) {
  const canUserDoAction = useCanUserDoOnProject("PROJECT_SETUP", project.id);
  const { modal, closeModal, openModal } = useModals<"edit" | "delete" | "export" | "duplicate">();
  const openEditModal = React.useCallback(() => openModal("edit"), [openModal]);
  const openDeleteModal = React.useCallback(() => openModal("delete"), [openModal]);
  const openDuplicateModal = React.useCallback(() => openModal("duplicate"), [openModal]);
  const exportSetupZip = useGet("/projects/:projectId(uuid)/setups/:setupId(uuid)/export");

  const handleExport = async () => {
    const res = await exportSetupZip({ routeParams: { projectId: project.id, setupId: setup.id } });
    download(
      res.body as unknown as Blob,
      generateExportFileName({
        filename: "setup",
        projectName: project.name,
        setupName: setup.name,
        extension: "zip",
      }),
    );
  };

  const hasSetupExportableElement =
    setup.simulationModel !== null ||
    setup.simulationInputs.length > 0 ||
    setup.simulationOutputs.length > 0;

  return (
    <>
      <Modal
        title={t("setups.modal.add_or_edit.create_setup.update")}
        isOpen={modal === "edit"}
        onRequestClose={closeModal}
      >
        <CreateUpdateModalForm
          onRequestClose={closeModal}
          projectId={project.id}
          setup={setup}
          setups={setups}
          timeZone={project.timezone}
          okButtonName={t("modalForm.button.name.edit")}
        />
      </Modal>
      <Modal
        title={t("setups.modal.duplicate_setup")}
        isOpen={modal === "duplicate"}
        onRequestClose={closeModal}
      >
        <DuplicateModalForm setup={setup} projectId={project.id} onRequestClose={closeModal} />
      </Modal>
      <Modal
        title={t("setups.modal.delete_setup")}
        isOpen={modal === "delete"}
        onRequestClose={closeModal}
      >
        <DeleteModalForm
          project={project}
          onRequestClose={closeModal}
          setup={setup}
          setups={setups}
        />
      </Modal>
      {canUserDoAction && (
        <StyledMenu
          arrowWidth={0}
          //   gap={16}
          placement="bottom"
          overlay={
            <>
              <OptionGroup>
                <Option closeOnClick onClick={openEditModal}>
                  <PencilLine color={color.carbon800} />
                  {t("project.setup.header.dropdown.edit_setup")}
                </Option>
                <Option closeOnClick onClick={openDuplicateModal}>
                  <FileCopyLine color={color.carbon800} />
                  {t("project.setup.header.dropdown.duplicate_setup")}
                </Option>
                {hasSetupExportableElement && (
                  <>
                    <Option closeOnClick onClick={handleExport}>
                      <>
                        <DownloadLine color={color.carbon800} />
                        {t("project.setup.header.dropdown.export_setup")}
                      </>
                    </Option>
                  </>
                )}
              </OptionGroup>
              <OptionGroup>
                <Option closeOnClick onClick={openDeleteModal}>
                  <DeleteBinLine color={color.carbon800} />
                  {t("project.setup.header.dropdown.delete_setup")}
                </Option>
              </OptionGroup>
            </>
          }
        >
          <ButtonIconV2 className={HIDE_ON_EXPORT}>
            <MoreFill />
          </ButtonIconV2>
        </StyledMenu>
      )}
    </>
  );
}

export default SecondaryDropdownMenu;
